import cloneDeep from 'lodash-es/cloneDeep';
import findLastIndex from 'lodash-es/findLastIndex';
import groupBy from 'lodash-es/groupBy';
import keyBy from 'lodash-es/keyBy';
import omit from 'lodash-es/omit';

import { ServerListViewChangeData } from '@/components/ServerListView/ServerListViewChangeData';
import { STATUSES, URLS } from '@/constants';
import { LISTING_ALLOCATION_TYPE } from '@/constants/options';
import { GoogleAddress } from '@/models/address';
import { Company, PromoCode } from '@/models/company';
import { Customer, CustomerGroup } from '@/models/customer';
import {
  CustomMenuCustomer,
  NewCustomMenu,
  UpdateCustomMenuCustomer,
  UpdatedCustomMenu,
  UpdatedCustomMenuItem,
} from '@/models/customMenus';
import {
  Batch,
  EditableInventory,
  InventoryFacility,
  InventoryItem,
  InventoryProduct,
  InventoryProductAssigned,
  InventoryProductWithAllocations,
  ProductInventory,
  Strain,
  WMSInventoryItem,
} from '@/models/inventory';
import {
  BuyerAssigned,
  GroupAssigned,
  Listing,
  ListingAllocation,
  ListingAllocationCallToAction,
  ListingAllocationType,
  Product,
  TimePeriod,
} from '@/models/product';
import { UserCompany } from '@/models/userCompany';
import notify from '@/notify';
import i18n from '@/plugins/i18n';
import * as crmApi from '@/services/api/CRM';
import * as inventoryApi from '@/services/api/inventory';
import { GetFacilitiesParams, GetProductsPaginatedParams } from '@/services/api/inventory';
import { CustomMenu, CustomMenuDetail } from '@/services/api/models/customMenus';
import * as inventoryParsers from '@/services/parsers/inventory';
import store from '@/store';
import { track } from '@/track';
import { handleError } from '@/utils/errors';
import { isNotEmptyObject } from '@/utils/isNotEmptyObject';
import sortByAvailability from '@/utils/sortByAvailability';

import { InventoryState } from './inventory.state';

interface ProductsParams {
  buyer?: Customer['id'];
  isBrandOwner?: boolean;
  orderByAvailable?: boolean;
  facilityId?: InventoryFacility['id'];
  [key: string]: unknown;
}

const updatePaginatedProductInventoryState = (
  state: InventoryState,
  updatedProductInventory: Partial<ProductInventory>,
) => {
  const productInventory = state.paginatedProductInventory.value.results.slice();

  const productIndex = productInventory.findIndex((product) => product.id === updatedProductInventory.id);

  const updatedProduct = {
    ...productInventory[productIndex],
    ...updatedProductInventory,
  };

  productInventory.splice(productIndex, 1, updatedProduct);

  state.paginatedProductInventory.value = {
    ...state.paginatedProductInventory.value,
    results: productInventory,
  };
};

export const setupInventoryActions = (state: InventoryState) => {
  const setProductsState = (products: Product[]) => {
    state.products.value = products;
  };

  /**
   * Load Products from the API; Products that have been previously fetched and
   * cached may be loaded directly, else fallback to localStorage.
   * @param params - additional query parameters for the fetch request
   * @param params.orderByAvailable - optional param to sort alphabetically and move unavailable to the bottom
   * @param params.facilityId - optional param for fetching products in a given facility
   * @param additionalProperties - additional properties to merge into product
   */
  const fetchProducts = async (params: ProductsParams = {}, additionalProperties: Partial<Product> = {}) => {
    try {
      state.isLoadingProducts.value = true;

      const { orderByAvailable = false, facilityId, ...queryParams } = { ...params };

      let products: (Product | ProductInventory)[] = [];

      try {
        if (facilityId) {
          products = await inventoryApi.getProductsInFacility({ facilityId, ...queryParams });
          products = inventoryParsers.updateWithInvItemQuantities(products as ProductInventory[]);
        } else {
          products = await inventoryApi.getProducts(queryParams);
        }
      } catch (error) {
        handleError(error, i18n.t('inventory.notify.errorProductApi'));
      }

      if (orderByAvailable) {
        products = sortByAvailability({ products, additionalProperties });
      }

      state.products.value = products;

      return products;
    } finally {
      state.isLoadingProducts.value = false;
    }
  };

  function fetchProductCount(params: { [key: string]: unknown } = {}) {
    return inventoryApi.getProductCount(params);
  }

  /**
   * Update product in store state
   * @param product - product to be updated in state
   */
  // eslint-disable-next-line require-await
  const updateProductState = async (updatedProduct: Partial<Product>) => {
    const productIndex = state.products.value.findIndex((product) => product.id === updatedProduct.id);
    const _product = {
      ...state.products.value[productIndex],
      ...updatedProduct,
    };

    state.products.value.splice(productIndex, 1, _product);
  };

  /**
   * Update product via API, and update product in store state
   * @param payload
   * @param payload.product - product to update
   * @param payload.successMessage - message displayed in toastr on success
   * @param payload.trackEvent - event sent to segment on success
   */
  const updateProduct = async ({
    product,
    successMessage,
    trackEvent,
  }: {
    product: Product;
    successMessage: string;
    trackEvent: string;
  }) => {
    const response = await inventoryApi.updateProduct(product);

    updateProductState(response);

    if (response.childrenCount) {
      fetchVarieties([product.id]);
    }

    if (successMessage) {
      notify.success(successMessage);
    }

    if (trackEvent) {
      track(trackEvent);
    }
  };

  /**
   * Update product and commits to the paginatedProductInventory state
   */
  const updatePaginatedProductInventory = async (product: Partial<Product> & { id: Product['id'] }) => {
    const response = await inventoryApi.updateProduct(product);

    updatePaginatedProductInventoryState(state, response);
    notify.success(i18n.t('notify.productUpdated'));
  };

  /**
   * Updates the product or child product in the product collection
   */
  const updateActiveProduct = async (product: inventoryApi.PatchActiveProductPayload) => {
    const updatedProduct = await inventoryApi.patchActiveProduct(product);

    // get the updated product index. If it's a child product, use the parent's index
    const productList = state.paginatedProducts.value.results.slice();
    const productIndex = productList.findIndex((product) => {
      return updatedProduct.parent ? product.id === updatedProduct.parent.id : product.id === updatedProduct.id;
    });

    // if updated product is a child product (variety)
    //   1. update the child object on the parent product, and
    //   2. update the parent listing state if different (as per product.parent.listingState)
    if (updatedProduct.parent && !updatedProduct.children?.length) {
      const parentProduct = productList[productIndex];
      const { children } = parentProduct;
      const updatedChildIndex = children.findIndex((child) => child.id === updatedProduct.id);

      // update the child
      children.splice(updatedChildIndex, 1, updatedProduct);

      // update the parent's listingState if changed.
      // this only happens when a parent becomes available because the child is updated to available.
      if (parentProduct.listingState !== updatedProduct.parent.listingState) {
        parentProduct.listingState = updatedProduct.parent.listingState;
      }
      // if updated product is a standalone or parent product
    } else {
      productList.splice(productIndex, 1, updatedProduct);
    }

    state.paginatedProducts.value.results = productList;
  };

  /**
   * Fetches a company's Product Lines
   */
  const fetchProductLines = async (data: { isBrandOwner: boolean } = { isBrandOwner: false }) => {
    const productLines = await inventoryApi.getProductLines(data);

    state.productLines.value = productLines;
  };

  /**
   * Fetch the product listing states.
   */
  const fetchListingStates = async () => {
    const listingStates = await inventoryApi.getListingStates();

    state.listingStates.value = listingStates;
  };

  /**
   * Fetches custom menus from the API
   * @param skipLoading - whether to skip updating loading state
   */
  const fetchCustomMenuList = async ({ skipLoading = false }: { skipLoading?: boolean } = {}) => {
    try {
      if (!skipLoading) {
        state.isLoadingCustomMenuList.value = true;
      }

      const response = await inventoryApi.getCustomMenuList();

      state.customMenuList.value = response.results;
    } finally {
      state.isLoadingCustomMenuList.value = false;
    }
  };

  /**
   * Fetches custom menu by passed-in id
   */
  const fetchCustomMenuById = async (id: CustomMenuDetail['id'], options: { skipLoading?: boolean } = {}) => {
    if (!options.skipLoading) {
      state.isLoadingCustomMenu.value = true;
    }

    const response = await inventoryApi.getCustomMenuById(id);

    state.customMenu.value = response;
    state.isLoadingCustomMenu.value = false;

    return response;
  };

  /**
   * Gets the static list of product categories possible for all products in LeafLink
   */
  async function fetchCategories() {
    const response = await inventoryApi.getCategories();

    state.categories.value = response;
    state.productCategories.value = response;

    return response;
  }

  /**
   * Adds a new custom menu
   * @param customMenu - custom menu to add
   */
  const addCustomMenu = async (newCustomMenu: NewCustomMenu) => {
    const customMenuResponse = await inventoryApi.createCustomMenu(newCustomMenu);

    // add customers
    if (newCustomMenu.priceScheduleCustomers) {
      const customersToUpdate = newCustomMenu.priceScheduleCustomers.map((customer) => {
        return {
          ...customer,
          priceScheduleId: customMenuResponse.id,
        };
      });
      await crmApi.updateCustomersBulk(customersToUpdate);
    }

    // add products
    if (newCustomMenu.priceScheduleItems.length) {
      const productsToUpdate = newCustomMenu.priceScheduleItems.map((product) => {
        return {
          ...product,
          priceSchedule: customMenuResponse.id,
        };
      });
      await saveCustomMenuProducts(productsToUpdate);
    }

    notify.success(i18n.t('inventory.notify.customMenuAdded', { name: newCustomMenu.name }));

    return customMenuResponse;
  };

  /**
   * Removes a custom menu
   * @param customMenu - custom menu to delete
   */
  const deleteCustomMenu = async (customMenu: CustomMenu) => {
    await inventoryApi.removeCustomMenu(customMenu.id).catch((error) => {
      handleError(error, i18n.t('inventory.custom_menus.delete_error'));
    });

    const menuIndex = state.customMenuList.value.findIndex((menu) => menu.id === customMenu.id);

    state.customMenuList.value.splice(menuIndex, 1);
    notify.success(i18n.t('inventory.notify.customMenuRemoved', { name: customMenu.name }));
  };

  /**
   * Update custom menu meta data
   */
  const updateCustomMenu = async (customMenu: CustomMenu | UpdatedCustomMenu) => {
    if ('products' in customMenu && customMenu.products?.length) {
      await saveCustomMenuProducts(customMenu.products);
    }

    await inventoryApi.updateCustomMenu(customMenu).catch((error) => {
      handleError(error, i18n.t('inventory.custom_menus.update_error'));
    });

    notify.success('Custom Menu updated.');
  };

  /**
   * Update custom menu products
   * @param products - products to add/update/remove from custom menu
   */
  const saveCustomMenuProducts = async (products: UpdatedCustomMenuItem[]) => {
    const groups = groupBy(products, (product) => product.eventType);
    const newProducts = groups.add || [];
    const updatedProducts = groups.update || [];
    const removedProducts = groups.remove || [];

    if (newProducts.length) {
      await inventoryApi.addCustomMenuProducts(newProducts);
    }

    if (updatedProducts.length) {
      await inventoryApi.updateCustomMenuProducts(updatedProducts.map((item) => omit(item, 'product')));
    }

    for (const product of removedProducts) {
      await inventoryApi.removeCustomMenuProduct(product);
    }
  };

  /**
   * Add or remove customers from a custom menu
   * @param payload
   * @param payload.customers - list of customers to be updated
   * @param payload.scheduleId - custom menu id to add the customers to
   * @param payload.existingCustomers - list of existing customers for a Custom Menu
   */
  const updateCustomMenuCustomers = async (data: {
    customers: UpdateCustomMenuCustomer[];
    existingCustomers: CustomMenuCustomer[];
    scheduleId: CustomMenuDetail['id'];
  }) => {
    const { customers, existingCustomers, scheduleId } = data;

    const modifiedCustomers: UpdateCustomMenuCustomer[] = [];

    const existingIds = keyBy(existingCustomers, 'id');

    customers.forEach((customer) => {
      if (existingIds[customer.id] && customer.priceScheduleId !== existingIds[customer.id]?.priceSchedule) {
        modifiedCustomers.push(customer);
      }

      if (!existingIds[customer.id] && customer.priceScheduleId !== null) {
        modifiedCustomers.push(customer);
      }
    });

    await crmApi.updateCustomersBulk(modifiedCustomers);

    // re-fetch custom menu to update customers
    await fetchCustomMenuById(scheduleId, { skipLoading: true });

    // TODO: Replace with Pinia CRM action
    store.commit('CRM/UPDATE_CUSTOMERS_PRICE_SCHEDULE', modifiedCustomers, { root: true });

    notify.success(i18n.t('inventory.notify.customersUpdated'));
  };

  /**
   * Fetches Promo Codes from the API. Promo Codes that have been previously
   * fetched and cached may be loaded directly from localStorage.
   */
  const fetchPromos = async () => {
    state.isLoadingPromoCodes.value = true;
    const promoCodes = await inventoryApi.getPromoCodes();

    state.promoCodes.value = promoCodes;
    state.isLoadingPromoCodes.value = false;

    return promoCodes;
  };

  /**
   * Creates a new Promo Code. Updates the store if successful.
   * @param data - an object with new promo code data
   */
  const savePromo = async (data: PromoCode) => {
    let code: PromoCode;

    // TODO: endpoint should receive ISO format string yyyy-mm-dd
    data.startDate = new Date(data.startDate).toISOString();
    data.endDate = data.endDate ? new Date(data.endDate).toISOString() : null;

    if (data.id) {
      // update existing
      code = await inventoryApi.updatePromo(data).catch((error) => {
        handleError(error, i18n.t('inventory.notify.errorUpdatePromo'));
      });
      notify.success(i18n.t('inventory.notify.promotionUpdated'));
    } else {
      code = await inventoryApi.addPromo(data);
      notify.success(i18n.t('inventory.notify.promotionAdded'));
    }

    const idx = state.promoCodes.value.findIndex((p) => p.id === code.id);

    code.startDate = code.startDate.substring(0, 10);
    code.endDate = code.endDate ? code.endDate.substring(0, 10) : null;

    if (!~idx) {
      state.promoCodes.value.push(code);
    } else {
      state.promoCodes.value.splice(idx, 1, code);
    }

    return code;
  };

  /**
   * Removes a Promo Code.
   * @param data - promo code to delete
   */
  const removePromo = async (code: PromoCode) => {
    await inventoryApi.removePromo(code);

    state.promoCodes.value.splice(state.promoCodes.value.indexOf(code), 1);

    notify.success(i18n.t('inventory.notify.promotionRemoved'));
  };

  /**
   * Update product inventory
   * @param product - product to update
   */
  const syncProductInventory = async (product: Product) => {
    const response = await inventoryApi.updateProduct(product);

    updateProductState(response);

    notify.success(i18n.t('inventory.notify.productInventoryUpdated'));
  };

  /**
   * Fetches varieties from the API.
   * @param productIds - ids of the products to fetch varieties
   */
  const fetchVarieties = async (productIds: Product['id'][]) => {
    const varieties = await inventoryApi.getVarieties(productIds);

    varieties.forEach((variety) => {
      updateProductState(variety);
    });
  };

  /**
   * Load Products from the API; Products that have been previously fetched and
   * cached may be loaded directly, else fallback to localStorage.
   * @param productId - product to fetch the orders for
   */
  const fetchProductOrders = async (productId: Product['id']) => {
    const params = {
      archived: false,
      fields_include: ['orders'].join(','),
    };
    const data = {
      params: params,
      productId: productId,
    };
    const product = await inventoryApi
      .getProductOrders(data)
      .catch((error) => handleError(error, i18n.t('inventory.notify.errorProductApi')));

    updateProductState(product);
  };

  /**
   * Patches product list to API
   * @param products - products to patch
   */
  const bulkPatchProducts = async (products: { sku: string; quantity: number }[]) => {
    const response = await inventoryApi.bulkPatchProducts(products);

    response?.forEach((product) => {
      updateProductState(product);
    });
  };

  /**
   * Fetch companies eligible to add choosen product to the cart
   * @param productId - the product ID to fetch eligible companies for
   */
  // eslint-disable-next-line require-await
  const fetchEligibleCompanies = async (productId: Product['id']) => {
    return inventoryApi.getEligibleCompanies(productId);
  };

  /**
   * Assigns customers and customer groups to products (used in Listings page v1)
   * @param payload - call to action, customer ids, group ids, product ids to update, timePeriod
   */
  const updateListings = async (payload: {
    callToAction: ListingAllocationCallToAction;
    customersById: Record<number, Customer>;
    customerIds: Customer['id'][];
    groupIds: CustomerGroup['id'][];
    productIds: Product['id'][];
    timePeriod: TimePeriod;
  }) => {
    const { callToAction, customerIds, customersById, groupIds, productIds, timePeriod } = payload;

    const expirationDate = timePeriod?.quantity
      ? inventoryParsers.convertTimePeriodToDate(Number(timePeriod.quantity), timePeriod.period)
      : null;

    const partnerIds = inventoryParsers.mapPartnerIds(customerIds, customersById);
    const isMissingPartner = partnerIds.some((id) => !id);

    if (isMissingPartner) {
      notify.error(i18n.t('inventory.notify.errorUpdatingListing'));

      return;
    }

    const assignedBuyers: BuyerAssigned[] = (partnerIds as NonNullable<Customer['partnerId']>[]).map((buyerId) => ({
      buyerId,
      expirationDate,
      callToAction,
      quantity: 0,
    }));

    const assignedGroups: GroupAssigned[] = groupIds.map((customerGroupId) => ({
      customerGroupId,
      expirationDate,
      callToAction,
      quantity: 0,
    }));

    await inventoryApi.updateListings({
      assignedBuyers,
      assignedGroups,
      productIds,
      isExclusive: true,
    });
    notify.success(i18n.t('inventory.notify.listingSuccessfullyUpdated'));

    return 'success';
  };

  /**
   * Updates listing allocations (used in Listings page v2)
   */
  const updateListingAllocations = async (payload: {
    customersById: Record<number, Customer>;
    customerGroupsById: Record<number, CustomerGroup>;
    productId: number;
    productAllocations: ListingAllocation[];
    isExclusive: boolean;
  }) => {
    const { customersById, customerGroupsById, productId, productAllocations, isExclusive = false } = payload;

    const buyersFromAllocations = productAllocations
      .filter((allocation) => allocation.item.buyerId && customersById[allocation.item.buyerId]?.partnerId)
      .map((allocation) => ({
        ...allocation.item,
        buyerId: customersById[allocation.item.buyerId as number].partnerId,
      }));

    const groupsFromAllocations = productAllocations
      .filter(
        (allocation) => allocation.item.customerGroupId && customerGroupsById[allocation.item.customerGroupId]?.id,
      )
      .map((allocation) => ({
        ...allocation.item,
        customerGroupId: customerGroupsById[allocation.item.customerGroupId as number].id,
      }));

    const listings: Listing = {
      assignedBuyers: buyersFromAllocations as BuyerAssigned[],
      assignedGroups: groupsFromAllocations as GroupAssigned[],
      productIds: [productId],
      isExclusive,
    };

    await inventoryApi.updateListings(listings);

    const productListings = state.paginatedListings.value.results.slice();
    const productIndex = productListings.findIndex((product) => product.id === productId);
    const updatedProduct = {
      ...productListings[productIndex],
      allocations: productAllocations,
    };

    productListings.splice(productIndex, 1, updatedProduct);

    state.paginatedListings.value.results = productListings;

    notify.success(i18n.t('inventory.notify.listingSuccessfullyUpdated'));
  };

  /**
   * Gets list of products assigned to customers
   * @param params - query string
   * @deprecated
   */
  const fetchProductsAssigned = async ({
    serializedParams,
    activeCompany,
    customersByPartnerId,
  }: {
    serializedParams: GetProductsPaginatedParams['serialized'];
    activeCompany: Company;
    customersByPartnerId: Record<number, Customer>;
  }) => {
    state.isLoadingProductsAssigned.value = true;

    if (!isNotEmptyObject(activeCompany)) {
      throw new Error('No active company');
    }

    const response = await inventoryApi.getProductsPaginated<InventoryProduct>({
      endpoint: URLS.LISTINGS.PRODUCTS,
      isCompanySpecific: true,
      serialized: serializedParams,
      nonListViewSerializedParams: 'include_children=images&archived=false',
    });

    const mappedProducts: InventoryProductAssigned[] = response.results.map((product) => {
      let timePeriod: TimePeriod | null = null;
      let callToAction: string | null = null;

      let shouldShowInCases = false;

      if (activeCompany.manageInventoryInCases) {
        shouldShowInCases = product.unitMultiplier > 1;
      }

      // assignments are one-to-one with product
      const assignment = product.assignments?.length ? product.assignments[0] : undefined;

      const buyers: number[] = [];
      let groups: number[] = [];

      if (assignment) {
        assignment.assignedBuyers.forEach((item) => {
          const partner = customersByPartnerId[item.buyerId];

          if (partner) {
            buyers.push(customersByPartnerId[item.buyerId].id);
          }
        });

        groups = assignment.assignedGroups.map((item) => item.customerGroupId);

        // expiration date and call to action come in every object inside assignedBuyers or assignedGroups
        // that is why we have to get them from the first occurrence
        const buyer = assignment?.assignedBuyers?.[0];
        const group = assignment?.assignedGroups?.[0];

        if (assignment.assignedBuyers.length && buyer?.expirationDate) {
          timePeriod = inventoryParsers.getTimePeriod(buyer.expirationDate);
          callToAction = buyer.callToAction;
        } else if (assignment.assignedGroups.length && group?.expirationDate) {
          timePeriod = inventoryParsers.getTimePeriod(group.expirationDate);
          callToAction = group.callToAction;
        }
      }

      return {
        ...product,
        sanitizedDisplayName: product.displayName.replace(/[,()-.]/g, '').replace(/  +/g, ' '),
        numberInCase: shouldShowInCases ? product.unitMultiplier : 1,
        shouldShowInCases,
        isInherited: product.inventoryManagement === STATUSES.INVENTORY_MANAGEMENT.INHERITED,
        isParent: !!product.childrenCount,
        isChild: !!product.parent,
        isUnlimited: product.inventoryManagement === STATUSES.INVENTORY_MANAGEMENT.UNLIMITED,
        isExclusive: !!assignment?.isExclusive,
        customers: buyers,
        groups,
        timePeriod,
        callToAction,
      };
    });

    state.paginatedProductsAssigned.value = {
      count: response.count,
      prev: response.prev,
      next: response.next,
      results: mappedProducts,
    };

    state.isLoadingProductsAssigned.value = false;

    return state.paginatedProductsAssigned.value;
  };

  const fetchProductsWithListingAllocations = async ({
    serializedParams,
    activeCompany,
    customersByPartnerId,
  }: {
    serializedParams: GetProductsPaginatedParams['serialized'];
    activeCompany: Company;
    customersByPartnerId: Record<number, Customer>;
  }) => {
    state.isLoadingListings.value = true;

    const response = await inventoryApi.getProductsPaginated<InventoryProduct>({
      endpoint: URLS.LISTINGS.PRODUCTS,
      isCompanySpecific: true,
      serialized: serializedParams,
      nonListViewSerializedParams: 'include_children=images&archived=false',
    });

    const mappedProducts: InventoryProductWithAllocations[] = response.results.map((product) => {
      let shouldShowInCases = false;

      if (activeCompany.manageInventoryInCases) {
        shouldShowInCases = product.unitMultiplier > 1;
      }

      // assignments are one-to-one with product
      const assignment = product.assignments?.length ? product.assignments[0] : undefined;
      const buyers: ListingAllocation[] = [];
      let groups: ListingAllocation[] = [];

      if (assignment) {
        assignment.assignedBuyers.forEach((item) => {
          const partner = customersByPartnerId[item.buyerId];

          if (partner) {
            buyers.push({
              item: {
                buyerId: customersByPartnerId[item.buyerId].id,
                callToAction: item.callToAction,
                expirationDate: item.expirationDate,
                quantity: Number(item.quantity) || 0,
              },
              type: LISTING_ALLOCATION_TYPE.BUYER as ListingAllocationType,
            });
          }
        });

        groups = assignment.assignedGroups.map((item) => {
          return {
            item: {
              customerGroupId: item.customerGroupId,
              callToAction: item.callToAction,
              expirationDate: item.expirationDate,
              quantity: Number(item.quantity) || 0,
            },
            type: LISTING_ALLOCATION_TYPE.GROUP as ListingAllocationType,
          };
        });
      }

      return {
        ...product,
        sanitizedDisplayName: product.displayName.replace(/[,()-.]/g, '').replace(/  +/g, ' '),
        numberInCase: shouldShowInCases ? product.unitMultiplier : 1,
        shouldShowInCases,
        isInherited: product.inventoryManagement === STATUSES.INVENTORY_MANAGEMENT.INHERITED,
        isParent: !!product.childrenCount,
        isChild: !!product.parent,
        isUnlimited: product.inventoryManagement === STATUSES.INVENTORY_MANAGEMENT.UNLIMITED,
        isExclusive: !!assignment?.isExclusive,
        allocations: groups.concat(buyers),
      };
    });

    state.paginatedListings.value = {
      count: response.count,
      prev: response.prev,
      next: response.next,
      results: mappedProducts,
    };
    state.isLoadingListings.value = false;

    return state.paginatedListings.value;
  };

  const getProductInventoryParams = () => {
    const includedFields = [
      'created_on',
      'display_name',
      'inventory_items',
      'license',
      'quantity',
      'reserved_qty',
      'sku',
      'unit_of_measure',
    ].join(',');

    return `fields_include=${includedFields}&archived=false&include_children=images`;
  };

  /**
   * Gets products with inventory items, and commits them to store in shape of PaginatedResponse
   * @param serializedParams - params surfaced from ServerListView
   * @param activeCompany - active user company
   */
  const fetchProductInventory = async ({
    serializedParams,
    activeCompany,
  }: {
    serializedParams: GetProductsPaginatedParams['serialized'];
    activeCompany: UserCompany;
  }) => {
    state.isLoadingProductInventory.value = true;

    let wmsProductInventory: WMSInventoryItem[] = [];

    try {
      const { results, ...response } = await inventoryApi.getProductsPaginated<ProductInventory>({
        endpoint: URLS.INVENTORY.INVENTORY_MANAGEMENT,
        serialized: serializedParams,
        nonListViewSerializedParams: getProductInventoryParams(),
      });

      // Fetch WMS Product Lot Inventory for Brand Fulfillment customers
      if (activeCompany.enableBrandFulfillmentLotInventoryVisibility) {
        const productIds = results.map((product) => product.id);

        wmsProductInventory = await fetchProductWmsInventory({ productIds });
      }

      // filter out inventory item that is 1:1 copy of the product
      const products = inventoryParsers.parseProductInventory(results, wmsProductInventory);

      state.paginatedProductInventory.value = {
        count: response.count,
        next: response.next,
        prev: response.prev,
        results: products,
      };
    } finally {
      state.isLoadingProductInventory.value = false;
    }
  };

  async function fetchProductWmsInventory(payload: { productIds: Product['id'][] }) {
    return await inventoryApi.getWmsProductInventory(payload.productIds);
  }

  /**
   * Gets products with inventory items, and commits them to store in shape of PaginatedResponse
   * @param payload - params surfaced from ServerListView
   */
  const exportProductInventory = async ({ serialized }: { serialized: ServerListViewChangeData['serialized'] }) => {
    const { results } = await inventoryApi.getProductsPaginated<ProductInventory>({
      endpoint: URLS.INVENTORY.INVENTORY_MANAGEMENT,
      serialized,
      nonListViewSerializedParams: getProductInventoryParams(),
    });

    // filter out inventory item that is 1:1 copy of the product
    return inventoryParsers.parseProductInventory(results);
  };

  /**
   * Get list of batches associated to a product
   * @param payload
   * @param payload.productId - id of the product to fetch batches
   * @param payload.includeDeletedBatches - optional flag when request requires both active and inactive batches
   */
  const fetchProductBatches = async (payload: { productId: Product['id']; includeDeletedBatches?: boolean }) => {
    state.isLoadingAssociatedBatches.value = true;

    try {
      const associatedBatches = await inventoryApi.getProductBatches(payload);

      state.associatedBatches.value = associatedBatches;
    } finally {
      state.isLoadingAssociatedBatches.value = false;
    }
  };

  /**
   * Saves or updates an inventory item
   * @param item - inventory item to be created or updated
   */
  // eslint-disable-next-line require-await
  const saveInventoryItem = async (item: Partial<InventoryItem<number>>) => {
    // NOTE: To be used in new inventory page
    if (item.id) {
      return inventoryApi.updateInventoryItem(item);
    } else {
      return inventoryApi.createInventoryItem(item);
    }
  };

  /**
   * Archives an Inventory Item (batch), updates the store
   * @param payload
   * @param payload.existingInventoryItems - list of inventory items
   * @param payload.inventoryItemId - id of inventory item to remove
   * @param payload.productId - id of product whose inventory items are being updated
   */
  const archiveInventoryItem = async ({
    inventoryItem,
    isBatchInventoryEnabled,
    productId,
  }: {
    inventoryItem: InventoryItem<Batch | null>;
    isBatchInventoryEnabled: boolean;
    productId: Product['id'];
  }) => {
    await inventoryApi.deleteInventoryItem(inventoryItem.id);
    notify.success(i18n.t('inventory.archiveInventoryItem.successConfirmation'));

    const existingInventoryItems =
      state.paginatedProductInventory.value.results.find((product) => product.id === productId)?.inventoryItems || [];

    let updatedInventoryItems: typeof existingInventoryItems;

    // strictly batch-level inventory items have an internal default facility assocation
    const allInventoryItemsInFacility = existingInventoryItems.filter(
      (item) =>
        typeof item.warehouse === 'object' &&
        typeof inventoryItem.warehouse === 'object' &&
        item.warehouse.id === inventoryItem.warehouse.id,
    );

    const isLastInventoryItemInFacility = allInventoryItemsInFacility.length === 1;

    // With only batch-level inventory enabled, maintain a default non-batch inventory item after archiving the last batch
    // With facility inventory enabled, maintain a default inventory item per facility.
    if (isBatchInventoryEnabled && isLastInventoryItemInFacility) {
      updatedInventoryItems = existingInventoryItems.map((item) => {
        if (item.id === inventoryItem.id && (item.isDefault || isLastInventoryItemInFacility)) {
          return {
            ...item,
            batch: null,
            isDefault: true,
            quantity: '0',
            reservedQty: '0',
          };
        }

        return item;
      });
      // filters out additional batches
    } else {
      updatedInventoryItems = existingInventoryItems.filter((item) => {
        return item.id !== inventoryItem.id;
      });
    }

    const { quantity, reservedQty } = updatedInventoryItems.reduce(
      (prev, current) => {
        return {
          quantity: prev.quantity + parseFloat(String(current.quantity)),
          reservedQty: prev.reservedQty + parseFloat(current.reservedQty),
        };
      },
      {
        quantity: 0,
        reservedQty: 0,
      },
    );

    updatePaginatedProductInventoryState(state, {
      id: productId,
      quantity: quantity.toString(),
      reservedQty: reservedQty.toString(),
      inventoryItems: updatedInventoryItems,
    });
  };

  /**
   * Attempts to find the default inventory item associated with a given editable inventory item
   */
  function getDefaultInventoryItem(itemsToUpdate: EditableInventory[], product: ProductInventory) {
    for (const [itemToUpdateIndex, itemToUpdate] of itemsToUpdate.entries()) {
      for (const invItem of product.inventoryItems) {
        // TODO - address the InventoryItem interface implementations to prevent errors from a number/object union
        // like we see here when trying to access the id property
        if (
          typeof invItem.warehouse !== 'number' &&
          invItem.warehouse.id === itemToUpdate.facility.id &&
          !invItem.batch
        ) {
          return {
            itemToUpdate,
            itemToUpdateIndex,
            invItem,
          };
        }
      }
    }

    return null;
  }

  /**
   * Transforms a collection of EditableInventory to InventoryItem and creates / updates them; updates store
   * @param payload
   * @param payload.product - product to update
   * @param payload.itemsToUpdate - inventory items to update
   */
  const saveTransformedInventoryItems = async ({
    product,
    itemsToUpdate,
  }: {
    product: ProductInventory;
    itemsToUpdate: EditableInventory[];
  }) => {
    const _itemsToUpdate = cloneDeep(itemsToUpdate);
    const newProduct = cloneDeep(product);
    const hasBatches = itemsToUpdate.some((item) => item.batch);

    await Promise.all(
      _itemsToUpdate.map(async (item) => {
        let response: InventoryItem<Batch | null>;

        const inventoryItem: Partial<InventoryItem<number>> = {
          id: item.inventoryItemId,
          quantity: item.updatedQuantity,
          product: product.id,
          inventory: item.inventoryId,
          batch: item.batch?.id,
          warehouse: item.facility?.id,
        };

        if (inventoryItem.id) {
          response = await inventoryApi.updateInventoryItem(inventoryItem);
          notify.success(i18n.t('inventory.inventoryOverview.updated'));
        } else {
          response = await inventoryApi.createInventoryItem(inventoryItem);
          notify.success(i18n.t('inventory.inventoryOverview.created'));
        }

        // TODO: Remove most of what is below and refetch the product's inventory https://leaflink.atlassian.net/browse/DANK-324

        response.batch = item.batch;
        response.warehouse = item.facility;

        const defaultItem = hasBatches ? getDefaultInventoryItem(_itemsToUpdate, newProduct) : null;

        const existingInventoryItemIndex = newProduct.inventoryItems.findIndex(
          (inventoryItem) => inventoryItem.id === response.id,
        );

        // If new inventory item, and default exists, replace default
        if (existingInventoryItemIndex === -1 && defaultItem) {
          const existingDefaultItemIndex = newProduct.inventoryItems.findIndex(
            (inventoryItem) => inventoryItem.id === defaultItem.invItem.id,
          );

          newProduct.inventoryItems[existingDefaultItemIndex] = response;

          // If new inventory item, and default does not exist,
          // find the last instance of an existing inventory item under the same facility and position after it,
          // otherwise push to the end
        } else if (existingInventoryItemIndex === -1) {
          const lastInventoryItemUnderSameFacilityIndex = findLastIndex(newProduct.inventoryItems, (inventoryItem) => {
            return (
              typeof inventoryItem.warehouse === 'object' &&
              typeof response.warehouse === 'object' &&
              inventoryItem.warehouse?.id === response.warehouse?.id
            );
          });

          if (lastInventoryItemUnderSameFacilityIndex >= 0) {
            newProduct.inventoryItems.splice(lastInventoryItemUnderSameFacilityIndex + 1, 0, response);
          } else {
            newProduct.inventoryItems.push(response);
          }

          // Update existing inventory item
        } else {
          newProduct.inventoryItems[existingInventoryItemIndex] = response;
        }

        return response;
      }),
    );

    // set the product.quantity and product.reservedQty to the sum of the inventory items
    const { quantity, reservedQty } = newProduct.inventoryItems.reduce(
      (prev, current) => {
        return {
          quantity: prev.quantity + parseFloat(current.quantity),
          reservedQty: prev.reservedQty + parseFloat(current.reservedQty),
        };
      },
      {
        quantity: 0,
        reservedQty: 0,
      },
    );

    updatePaginatedProductInventoryState(state, {
      ...newProduct,
      quantity: quantity.toString(),
      reservedQty: reservedQty.toString(),
    });
  };

  /**
   * Get all inventory items
   * If optional productId is passed in, get all inventory items specific to a product
   * If optional includeBatches is passed in, fetch batch information for inventory items
   * @param productId - product id to fetch inventory items of
   * @param includeBatches - include expandable batch information in response
   */
  const fetchInventoryItems = (params: {
    includeBatches?: boolean;
    productId?: Product['id'];
    facilityId: InventoryFacility['id'];
  }) => {
    return inventoryApi.getInventoryItems(params);
  };

  const setIsLoadingProducts = (value: boolean) => {
    state.isLoadingProducts.value = value;
  };

  /**
   * Get paginated unarchived products with expanded images, varieties, and varieties' images, for Active Products page
   * @param payload - params surfaced from ServerListView
   */
  const fetchActiveProducts = async ({ params, serialized }: ServerListViewChangeData) => {
    state.isLoadingProducts.value = true;

    const response = await inventoryApi.getProductsPaginated<InventoryProduct>({
      endpoint: URLS.INVENTORY.ACTIVE_PRODUCTS,
      params,
      serialized,
      nonListViewSerializedParams: 'archived=false',
    });

    const { count, next, prev, results } = response;

    state.paginatedProducts.value = {
      count,
      next,
      prev,
      results,
    };

    state.isLoadingProducts.value = false;
  };

  const emptyGoogleAddress = {
    address: '',
    city: '',
    country: '',
    state: '',
    unitNumber: '',
    zipcode: '',
    lat: 0,
    long: 0,
  };

  /**
   * Set selected google address in state
   * @param address - google address
   */
  // eslint-disable-next-line require-await
  const setSelectedGoogleAddress = async (address: GoogleAddress = emptyGoogleAddress) => {
    state.facilityManagement.value.selectedGoogleAddress = address;
  };

  const saveFacility = async (facility: Partial<InventoryFacility>) => {
    const address = facility.address;

    if (address && address.lat && address.long) {
      const latValues = address.lat.toString().split('.');
      const longValues = address.long.toString().split('.');

      // trimming lat and long values
      const trimmedLat = latValues[1].slice(0, 6);
      const trimmedLong = longValues[1].slice(0, 6);

      address.lat = Number(`${latValues[0]}.${trimmedLat}`);
      address.long = Number(`${longValues[0]}.${trimmedLong}`);
    }

    facility.address = address;

    let response: InventoryFacility;

    if (facility.id) {
      response = await inventoryApi.editFacility(facility);

      notify.success(i18n.t('settings.facilityManagement.updateSuccess'));
    } else {
      response = await inventoryApi.createFacility(facility);

      notify.success(i18n.t('settings.facilityManagement.createSuccess'));
    }

    await fetchFacilities({ ordering: '-created_on' });

    return response;
  };

  /**
   * Fetch paginated facilities
   * @param payload - params surfaced from ServerListView
   */
  const fetchFacilities = async (params: GetFacilitiesParams = {}) => {
    state.facilityManagement.value.isLoadingFacilities = true;

    const response = await inventoryApi.getFacilities(params);

    state.facilityManagement.value.paginatedFacilities = {
      count: response.count,
      next: response.next,
      prev: response.prev,
      results: response.results,
    };

    state.facilityManagement.value.isLoadingFacilities = false;

    return response;
  };

  const fetchStrainClassifications = async () => {
    const response = await inventoryApi.getStrainClassifications();
    const responseData = response.filter((classification) => classification.value);

    state.strainClassifications.value = responseData;

    return response;
  };

  const addStrain = async (strain: Partial<Strain>) => {
    const response = await inventoryApi.addStrain(strain);

    notify.success(i18n.t('inventory.strains.notify.strainAdded'));

    return response.data;
  };

  return {
    addCustomMenu,
    archiveInventoryItem,
    bulkPatchProducts,
    deleteCustomMenu,
    exportProductInventory,
    fetchActiveProducts,
    fetchCategories,
    fetchCustomMenuById,
    fetchCustomMenuList,
    fetchEligibleCompanies,
    fetchFacilities,
    fetchInventoryItems,
    fetchListingStates,
    fetchProductBatches,
    fetchProductInventory,
    fetchProductLines,
    fetchProductOrders,
    fetchProducts,
    fetchProductCount,
    fetchProductWmsInventory,
    fetchProductsAssigned,
    fetchProductsWithListingAllocations,
    fetchPromos,
    fetchStrainClassifications,
    removePromo,
    saveCustomMenuProducts,
    saveFacility,
    saveInventoryItem,
    savePromo,
    saveTransformedInventoryItems,
    setIsLoadingProducts,
    setProductsState,
    setSelectedGoogleAddress,
    syncProductInventory,
    updateCustomMenu,
    updateCustomMenuCustomers,
    updateListingAllocations,
    updateListings,
    updateProduct,
    updatePaginatedProductInventory,
    updateActiveProduct,
    updateProductState,
    addStrain,
  };
};
